import { Info } from "lucide-react";

/**
 * A soft explainer strip that sits beneath a live-data chart or panel.
 * Reads like a caption in a science exhibit — plain English, no jargon.
 *
 * Usage:
 *   <BaselineNote
 *     title="What's a normal Schumann reading?"
 *     baseline="7.83 Hz (the classic 'Earth pulse')."
 *     scale={[
 *       { label: "Quiet",     range: "0–20 pT",   note: "the everyday hum" },
 *       { label: "Amplified", range: "20–60 pT",  note: "sensitives often feel it" },
 *       { label: "Spike",     range: "60+ pT",    note: "geomagnetic weather shifting" },
 *     ]}
 *   />
 */
export default function BaselineNote({ title, baseline, scale = [], footnote }) {
  return (
    <div
      className="mt-4 rounded-xl p-5 md:p-6"
      style={{
        background: "rgba(212, 162, 76, 0.05)",
        border: "1px solid rgba(212, 162, 76, 0.18)",
      }}
      data-testid="baseline-note"
    >
      <div className="flex items-start gap-3">
        <Info size={14} className="text-[color:var(--gold)] mt-1 shrink-0" />
        <div className="w-full">
          <div className="label-overline mb-1">{title}</div>
          <p className="text-[color:var(--parchment-soft)] font-sans-body leading-relaxed text-sm">
            <span className="text-[color:var(--parchment)] font-medium">Baseline: </span>
            <span className="font-serif-italic">{baseline}</span>
          </p>
          {scale.length > 0 && (
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {scale.map((s) => (
                <li key={s.label} className="rounded-lg border border-[color:var(--gold)]/12 p-3">
                  <div className="label-overline text-[10px]" style={{ color: s.tone || "var(--gold)" }}>
                    {s.label}
                  </div>
                  <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-base">{s.range}</div>
                  <div className="font-serif-italic text-[color:var(--text-muted)] text-xs mt-1">{s.note}</div>
                </li>
              ))}
            </ul>
          )}
          {footnote && (
            <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs">
              {footnote}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
